import { NAMESPACES, expandIri, type NamespacePrefix } from './namespaces.js';
import { TERMS, TERM_LIST, type TermDefinition } from './terms.js';

/** A JSON-LD term definition as it appears inside an `@context`. */
export type ContextTermValue = string | { readonly '@id': string; readonly '@type'?: '@id' };
export type JsonLdContext = Readonly<Record<string, ContextTermValue>>;

function compactTermIri(definition: TermDefinition): string {
  for (const [prefix, base] of Object.entries(NAMESPACES)) {
    if (definition.iri.startsWith(base)) return `${prefix}:${definition.iri.slice(base.length)}`;
  }
  return definition.iri;
}

function contextValueFor(definition: TermDefinition): ContextTermValue {
  const id = compactTermIri(definition);
  // `@type: @id` is what lets a reference be written as a bare string
  // (`"precedes": "act:S0.2"`) instead of `{"@id": "act:S0.2"}`.
  return definition.kind === 'id' ? { '@id': id, '@type': '@id' } : id;
}

/**
 * The canonical `@context`. A superset of the skeleton in `scratchpad/SPEC.md`:
 * every term there keeps its exact definition, so the seed files' terms resolve
 * unchanged. Key order is fixed (namespaces, borrowed terms, then A→Z) because
 * export must be byte-stable.
 */
export const DPSE_CONTEXT: JsonLdContext = (() => {
  const context: Record<string, ContextTermValue> = {
    dpem: NAMESPACES.dpem,
    schema: NAMESPACES.schema,
    rdfs: NAMESPACES.rdfs,
    name: compactTermIri(TERMS.name),
    description: compactTermIri(TERMS.description),
  };
  const rest = TERM_LIST.filter((t) => t.term !== 'name' && t.term !== 'description').sort((a, b) =>
    a.term < b.term ? -1 : a.term > b.term ? 1 : 0,
  );
  for (const definition of rest) context[definition.term] = contextValueFor(definition);
  return context;
})();

export interface ContextConflict {
  readonly term: string;
  readonly expectedIri: string;
  readonly actualIri: string;
}

function iriOf(value: ContextTermValue): string {
  return expandIri(typeof value === 'string' ? value : value['@id']);
}

/**
 * Rejects a document whose `@context` redefines one of our terms — or the `dpem`
 * namespace itself — to a different IRI. Such a file uses our words for someone
 * else's meaning, and importing it would silently corrupt the graph. Unknown
 * extra terms are not a conflict here; the node schemas decide those.
 */
export function findContextConflicts(context: unknown): readonly ContextConflict[] {
  if (typeof context !== 'object' || context === null || Array.isArray(context)) return [];
  const conflicts: ContextConflict[] = [];
  for (const [key, raw] of Object.entries(context as Record<string, unknown>)) {
    if (typeof raw !== 'string' && !isTermObject(raw)) continue;
    const value = raw as ContextTermValue;
    const namespace = NAMESPACES[key as NamespacePrefix] as string | undefined;
    if (namespace !== undefined) {
      if (typeof value === 'string' && value !== namespace) {
        conflicts.push({ term: key, expectedIri: namespace, actualIri: value });
      }
      continue;
    }
    const expected = DPSE_CONTEXT[key];
    if (expected === undefined) continue;
    const expectedIri = iriOf(expected);
    const actualIri = iriOf(value);
    if (expectedIri !== actualIri) conflicts.push({ term: key, expectedIri, actualIri });
  }
  return conflicts;
}

function isTermObject(value: unknown): value is { '@id': string } {
  return (
    typeof value === 'object' &&
    value !== null &&
    !Array.isArray(value) &&
    typeof (value as { '@id'?: unknown })['@id'] === 'string'
  );
}
